import React, { useCallback, useEffect, useState } from "react";
import {
  X,
  CheckCircle,
  Clock,
  MapPin,
  Package,
  // Printer,
  Download,
  Lock,
  ShieldCheck,
  Paperclip,
  Plus,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { downloadPOHDocumentPdf } from "../../utils/generatePOHDocument";
import { shipmentAccessAPI } from "../../services/api";

type EmailTag = { email: string; role: "view" | "poh_handler" };

interface Props {
  poh: Record<string, any>;
  shipmentId: string;
  pohLevel?: 0 | 1 | 2;
  driverSignUrl: string | null;
  deliveryDriverSignUrl?: string | null;
  onClose: () => void;
}

const apiOrigin = (() => {
  try {
    const base = (import.meta as any).env?.VITE_API_BASE_URL || "";
    return base ? new URL(base).origin : "";
  } catch {
    return "";
  }
})();

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

const pohRef = (id: string) => `POH-${id.slice(-8).toUpperCase()}`;

const fmtGeo = (g?: { lat?: number; lng?: number; accuracy?: number }) =>
  g && g.lat != null && g.lng != null
    ? `${g.lat.toFixed(5)}, ${g.lng.toFixed(5)}${g.accuracy ? ` (±${Math.round(g.accuracy)}m)` : ""}`
    : null;

const fileUrl = (u: string) => (u.startsWith("http") ? u : `${apiOrigin}${u}`);

function SignatureSlot({
  role,
  signature,
  qrContent,
  qrLabel,
  locked,
}: {
  role: string;
  signature?: any;
  qrContent?: string | null;
  qrLabel?: string;
  locked?: boolean;
}) {
  const isSigned = !!signature?.signatureData && !!signature?.signerName;
  const geoStr = fmtGeo(signature?.geo);
  const atts = Array.isArray(signature?.attachments)
    ? signature.attachments
    : [];

  return (
    <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2 bg-white min-h-[160px]">
      <div className="flex items-start justify-between">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
          {role}
        </p>
        {locked && isSigned && (
          <span className="inline-flex items-center gap-0.5 text-[9px] text-gray-400">
            <Lock className="h-2.5 w-2.5" /> locked
          </span>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center border border-dashed border-gray-200 rounded bg-gray-50 min-h-[70px]">
        {isSigned ? (
          <img
            src={signature.signatureData}
            alt="signature"
            className="max-h-16 max-w-full object-contain"
          />
        ) : qrContent ? (
          <div className="flex flex-col items-center gap-1">
            <QRCodeSVG value={qrContent} size={80} level="M" />
            <p className="text-[9px] text-gray-400">
              {qrLabel ?? "Scan to sign"}
            </p>
          </div>
        ) : (
          <p className="text-[10px] italic text-gray-300">
            Sign here via QR / dashboard
          </p>
        )}
      </div>

      <div className="border-b border-gray-300 pb-0.5">
        <p className="text-[11px] text-gray-700 font-medium truncate">
          {signature?.signerName ?? ""}
        </p>
      </div>

      {isSigned ? (
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full w-fit">
            <CheckCircle className="h-2.5 w-2.5" />
            <span className="text-[9px] font-semibold uppercase tracking-wide">
              Signed
            </span>
          </div>
          {signature?.signedAt && (
            <p className="text-[9px] text-gray-400">
              {fmtDate(signature.signedAt)}
            </p>
          )}
          {(signature?.signerEmail || signature?.driverEmail) && (
            <p className="text-[9px] text-gray-500 truncate">
              {signature?.emailVerifiedAt && (
                <ShieldCheck className="inline h-2.5 w-2.5 mr-0.5 text-green-600" />
              )}
              {signature.signerEmail || signature.driverEmail}
            </p>
          )}
          {signature?.identityNumber && (
            <p className="text-[9px] text-gray-500">
              ID: <span className="font-mono">{signature.identityNumber}</span>
            </p>
          )}
          {geoStr && (
            <p className="text-[9px] text-gray-500 flex items-center gap-0.5">
              <MapPin className="h-2.5 w-2.5" />{" "}
              <span className="font-mono">{geoStr}</span>
            </p>
          )}
          {atts.length > 0 && (
            <div className="pt-1 border-t border-gray-100">
              <p className="text-[9px] text-gray-500 font-medium flex items-center gap-0.5 mb-0.5">
                <Paperclip className="h-2.5 w-2.5" /> {atts.length} proof
                file(s)
              </p>
              <ul className="space-y-0.5">
                {atts.slice(0, 5).map((a: any, i: number) => (
                  <li key={i} className="text-[9px]">
                    <a
                      href={fileUrl(a.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline truncate block"
                      title={a.originalName}
                    >
                      {a.originalName}
                    </a>
                  </li>
                ))}
                {atts.length > 5 && (
                  <li className="text-[9px] text-gray-400">
                    … +{atts.length - 5} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full w-fit">
          <Clock className="h-2.5 w-2.5" />
          <span className="text-[9px] font-semibold uppercase tracking-wide">
            Pending
          </span>
        </div>
      )}
    </div>
  );
}

const POHDocumentModal: React.FC<Props> = ({
  poh,
  shipmentId,
  pohLevel = 1,
  driverSignUrl,
  deliveryDriverSignUrl,
  onClose,
}) => {
  const { t } = useTranslation("shipments");
  // Level 2 is shipper + consignee only — no driver signatures on either phase.
  const hideDriver = pohLevel === 2;
  const shipmentViewUrl = `${window.location.origin}/shipment-clients-view?shipmentId=${shipmentId}`;
  const [emailTags, setEmailTags] = useState<EmailTag[]>([]);
  const [emailTagInput, setEmailTagInput] = useState("");
  const [savingEmails, setSavingEmails] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    shipmentAccessAPI.getAccessRequests(shipmentId)
      .then((res) => setEmailTags(res.data.data.allowedEmails ?? []))
      .catch(() => {});
  }, [shipmentId]);

  const autoSaveEmails = useCallback(async (tags: EmailTag[]) => {
    setSavingEmails(true);
    try { await shipmentAccessAPI.updateAllowedEmails(shipmentId, tags); }
    catch { /* silent */ }
    finally { setSavingEmails(false); }
  }, [shipmentId]);

  const addEmail = () => {
    const val = emailTagInput.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return;
    if (emailTags.some((t) => t.email === val)) return;
    const next = [...emailTags, { email: val, role: "poh_handler" as const }];
    setEmailTags(next);
    setEmailTagInput("");
    autoSaveEmails(next);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shipmentViewUrl); } catch { return; }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const shipment = poh.shipmentId as Record<string, any> | undefined;
  const pickup = poh.pickup as Record<string, any> | undefined;
  const delivery = poh.delivery as Record<string, any> | undefined;

  const pickupComplete = pickup?.status === "completed";
  const deliveryComplete = delivery?.status === "completed";
  const fullyComplete = pickupComplete && deliveryComplete;

  const pickupAddr = shipment?.pickup;
  const deliveryAddr = shipment?.delivery;

  const handleDownload = async () => {
    try {
      await downloadPOHDocumentPdf({ ...poh, pohLevel } as any);
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
          <h2 className="text-sm font-semibold text-gray-700">
            Proof of Handover Document
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download PDF
            </button>
            {/* <button
              type="button"
              disabled
              title="Use Download PDF instead"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed"
            >
              <Printer className="h-3.5 w-3.5" />
              Print
            </button> */}
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Client link + email access ── */}
        <div className="border-b border-gray-100 px-4 py-3 shrink-0 space-y-2">
          <div className="flex items-center gap-2">
            <input type="text" value={shipmentViewUrl} readOnly
              className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white text-blue-600 select-all" />
            <button type="button" onClick={copyLink}
              className={`px-3 py-1.5 text-xs rounded-md border font-medium transition-colors whitespace-nowrap ${linkCopied ? "bg-green-600 text-white border-green-600" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
              {linkCopied ? "Copied!" : "Copy Link"}
            </button>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-medium text-gray-700">
                  {emailTags.length > 0
                    ? `${emailTags.length} email${emailTags.length > 1 ? "s" : ""} with POH access`
                    : "Grant POH access by email"}
                </span>
              </div>
              {savingEmails && <span className="text-[10px] text-gray-400 animate-pulse">Saving…</span>}
            </div>
            {emailTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {emailTags.map((entry) => (
                  <span key={entry.email}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${entry.role === "poh_handler" ? "bg-purple-50 text-purple-800 border-purple-200" : "bg-blue-50 text-blue-800 border-blue-200"}`}>
                    <span>{entry.email}</span>
                    <span className={`text-[10px] px-1 rounded-full font-medium ${entry.role === "poh_handler" ? "bg-purple-200 text-purple-900" : "bg-blue-200 text-blue-900"}`}>
                      {entry.role === "poh_handler" ? "POH" : "Standard"}
                    </span>
                    <button type="button" title="Toggle role"
                      onClick={() => { const next = emailTags.map((e) => e.email === entry.email ? { ...e, role: (e.role === "view" ? "poh_handler" : "view") as "view" | "poh_handler" } : e); setEmailTags(next); autoSaveEmails(next); }}
                      className="hover:text-amber-600 transition-colors">
                      <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" /></svg>
                    </button>
                    <button type="button" onClick={() => { const next = emailTags.filter((e) => e.email !== entry.email); setEmailTags(next); autoSaveEmails(next); }} className="hover:text-red-600 transition-colors">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <input type="email" value={emailTagInput} onChange={(e) => setEmailTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmail(); } }}
                placeholder="email@example.com — press Enter to add"
                className="flex-1 text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 placeholder-gray-400" />
              <button type="button" onClick={addEmail} className="p-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-600">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5 print:p-0">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                  LC
                </div>
                <span className="text-lg font-bold text-gray-900 tracking-tight">
                  Live Cargo
                </span>
              </div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                Proof of Handover
              </p>
              <p className="text-2xl font-extrabold text-gray-900 font-mono tracking-tight">
                {pohRef(String(poh._id))}
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                {shipment?.shipmentNumber && (
                  <span>
                    Order{" "}
                    <strong className="text-gray-800">
                      #{shipment.shipmentNumber}
                    </strong>
                  </span>
                )}
                {shipment?.referenceNumber && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span>
                      Ref{" "}
                      <strong className="text-gray-800">
                        {shipment.referenceNumber}
                      </strong>
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                {fullyComplete ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded-full uppercase tracking-wide">
                    <Lock className="h-3 w-3" /> Complete & Locked
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full uppercase tracking-wide">
                    <Clock className="h-3 w-3" /> In Progress
                  </span>
                )}
              </div>
            </div>

            {driverSignUrl && !pickupComplete && (
              <div className="border-2 border-gray-200 rounded-xl p-3 text-center space-y-1 shrink-0">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                  Pickup — Driver QR
                </p>
                <QRCodeSVG value={driverSignUrl} size={100} level="M" />
              </div>
            )}
            {deliveryDriverSignUrl && !deliveryComplete && (
              <div className="border-2 border-gray-200 rounded-xl p-3 text-center space-y-1 shrink-0">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                  Delivery — Driver QR
                </p>
                <QRCodeSVG value={deliveryDriverSignUrl} size={100} level="M" />
              </div>
            )}
          </div>

          <hr className="border-gray-200" />

          <div className="grid grid-cols-3 gap-3 bg-gray-50 rounded-lg p-3">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 flex items-center gap-0.5 mb-1">
                <MapPin className="h-2.5 w-2.5 text-green-600" /> Pickup
              </p>
              <p className="text-xs font-semibold text-gray-800">
                {pickupAddr?.companyName || pickupAddr?.address?.street || "—"}
              </p>
              <p className="text-[10px] text-gray-500">
                {[
                  pickupAddr?.address?.city,
                  pickupAddr?.address?.country?.name ||
                    pickupAddr?.address?.country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 flex items-center gap-0.5 mb-1">
                <MapPin className="h-2.5 w-2.5 text-red-600" /> Delivery
              </p>
              <p className="text-xs font-semibold text-gray-800">
                {deliveryAddr?.companyName ||
                  deliveryAddr?.address?.street ||
                  "—"}
              </p>
              <p className="text-[10px] text-gray-500">
                {[
                  deliveryAddr?.address?.city,
                  deliveryAddr?.address?.country?.name ||
                    deliveryAddr?.address?.country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 flex items-center gap-0.5 mb-1">
                <Package className="h-2.5 w-2.5 text-gray-500" /> Cargo
              </p>
              {shipment?.totalQuantity != null && (
                <p className="text-xs font-semibold text-gray-800">
                  {shipment.totalQuantity} item
                  {shipment.totalQuantity !== 1 ? "s" : ""}
                </p>
              )}
              {shipment?.totalWeight != null && (
                <p className="text-[10px] text-gray-500">
                  · {shipment.totalWeight} kg
                </p>
              )}
              {shipment?.loadType && (
                <p className="text-[10px] text-gray-500">{shipment.loadType}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-800">
                Event 1 — Pickup
              </p>
              <span
                className={`text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide flex items-center gap-1 ${
                  pickupComplete
                    ? "bg-green-100 text-green-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {pickupComplete && <Lock className="h-2.5 w-2.5" />}
                {pickupComplete
                  ? `Locked ${fmtDate(pickup?.lockedAt) || ""}`
                  : hideDriver
                    ? "Sender Must Sign at Warehouse"
                    : "Both Must Sign at Warehouse"}
              </span>
            </div>
            <div className={`grid gap-3 ${hideDriver ? "grid-cols-1" : "grid-cols-2"}`}>
              <SignatureSlot
                role="Sender · Müüja"
                signature={pickup?.consignerSignature}
                locked={!!pickup?.lockedAt}
              />
              {!hideDriver && (
                <SignatureSlot
                  role="Pickup Driver · Lähtejuht"
                  signature={pickup?.driverSignature}
                  qrContent={
                    driverSignUrl && !pickupComplete ? driverSignUrl : null
                  }
                  qrLabel="Scan to sign"
                  locked={!!pickup?.lockedAt}
                />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-800">
                Event 2 — Delivery
              </p>
              <span
                className={`text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide flex items-center gap-1 ${
                  deliveryComplete
                    ? "bg-green-100 text-green-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {deliveryComplete && <Lock className="h-2.5 w-2.5" />}
                {deliveryComplete
                  ? `Locked ${fmtDate(delivery?.lockedAt) || ""}`
                  : hideDriver
                    ? "Recipient Must Sign at Delivery"
                    : "Both Must Sign at Recipient"}
              </span>
            </div>
            <div className={`grid gap-3 ${hideDriver ? "grid-cols-1" : "grid-cols-2"}`}>
              {!hideDriver && (
                <SignatureSlot
                  role="Delivery Driver · Sihtjuht"
                  signature={delivery?.driverSignature}
                  qrContent={
                    deliveryDriverSignUrl && !deliveryComplete
                      ? deliveryDriverSignUrl
                      : null
                  }
                  qrLabel="Scan to sign"
                  locked={!!delivery?.lockedAt}
                />
              )}
              <SignatureSlot
                role="Recipient · Ostja"
                signature={delivery?.customerSignature}
                locked={!!delivery?.lockedAt}
              />
            </div>
          </div>

          {Array.isArray(shipment?.pohStatusOverrides) &&
            shipment.pohStatusOverrides.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
                  {t("statusChange.overridesTitle")}
                </p>
                <ul className="space-y-1.5">
                  {(
                    shipment.pohStatusOverrides as Array<{
                      reason: string;
                      byName?: string;
                      at?: string;
                    }>
                  ).map((o, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[11px] text-amber-900"
                    >
                      <span className="mt-[1px] inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[9px] font-semibold uppercase tracking-wide shrink-0">
                        {t("statusChange.overrideBadge")}
                      </span>
                      <span className="leading-tight">
                        <span className="font-medium">
                          {t(`statusChange.skipped.${o.reason}`)}
                        </span>
                        {(o.byName || o.at) && (
                          <span className="text-amber-700">
                            {" · "}
                            {o.byName ? `${o.byName} · ` : ""}
                            {fmtDate(o.at)}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          <p className="text-[10px] text-gray-400 text-right">
            Created: {fmtDate(poh.createdAt)}
            {poh.updatedAt && poh.updatedAt !== poh.createdAt && (
              <> · Updated: {fmtDate(poh.updatedAt)}</>
            )}
          </p>
        </div>

      </div>
    </div>
  );
};

export default POHDocumentModal;
